import React, { useState } from 'react';
import { Flag, Wrench, Users, Brain, Zap, Check } from 'lucide-react';
import { FRANCHISE_BENEFITS, FRANCHISE_PACKAGE, CONTACT } from '../mock';
import PageHeader from '../components/PageHeader';
import { useToast } from '../hooks/use-toast';
import api from '../lib/api';
import useSiteSettings from '../hooks/useSiteSettings';

const ICONS = { Flag, Wrench, Users, Brain, Zap };

export default function Franchising() {
  const { toast } = useToast();
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', city: '', desired: '', timeframe: '', budget: '', experience: 'No', experienceDetail: '', interest: '' });
  const [submitting, setSubmitting] = useState(false);
  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const settings = useSiteSettings();

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/franchise', form);
      toast({ title: 'Application submitted!', description: 'We\u2019ll be in touch shortly to discuss next steps.' });
      setForm({ firstName: '', lastName: '', email: '', phone: '', city: '', desired: '', timeframe: '', budget: '', experience: 'No', experienceDetail: '', interest: '' });
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Could not submit application.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main>
      <PageHeader title="Franchise With Wrappus" breadcrumb="Franchising" heroImage={settings.franchisingHero} />

      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <div className="eyebrow mb-3">Franchise With Wrappus</div>
          <h2 className="section-title text-3xl md:text-5xl mb-6">A Proudly Canadian Franchise Opportunity</h2>
          <p className="text-gray-700 leading-relaxed mb-4">At Wrappus, we’re a homegrown Canadian brand built on bold flavours, real value, and deep community roots. As we expand coast-to-coast, we’re inviting passionate Canadians to join us in delivering the iconic Wrappus experience to neighborhoods across the country.</p>
          <p className="text-gray-700">Whether you’re in <em>Vancouver, Calgary, Toronto, or Halifax</em> — if you believe in good food, hard work, and local pride, this is your opportunity to drive something big.</p>
        </div>
      </section>

      <section className="py-20 bg-[#FDF3EC]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <h2 className="section-title text-3xl md:text-5xl text-center mb-12">Why Franchise with Wrappus?</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FRANCHISE_BENEFITS.map((b) => {
              const Icon = ICONS[b.icon];
              return (
                <div key={b.title} className="bg-white rounded-3xl p-8 hover:shadow-xl transition-shadow group">
                  <div className="w-14 h-14 rounded-2xl bg-[#ED2E2E] text-white flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                    <Icon size={22} />
                  </div>
                  <h3 className="font-heading font-extrabold text-lg text-[#0F1C2E] mb-3">{b.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{b.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <h2 className="section-title text-3xl md:text-5xl text-center mb-4">What’s in Your Franchise Package?</h2>
          <p className="text-center text-gray-600 mb-10">Everything you need to launch fast and succeed long-term — built right here in Canada.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {FRANCHISE_PACKAGE.map((p) => (
              <div key={p} className="flex items-center gap-3 bg-[#FDF3EC] rounded-2xl px-5 py-4">
                <div className="w-8 h-8 rounded-full bg-[#ED2E2E] text-white flex items-center justify-center shrink-0"><Check size={16} strokeWidth={3} /></div>
                <span className="font-semibold text-[#0F1C2E]">{p}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#0F1C2E] text-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="section-title text-white text-3xl md:text-5xl mb-6">Who’s This For?</h2>
          <p className="text-white/70 mb-8">We’re looking for driven, community-minded Canadians who:</p>
          <ul className="grid sm:grid-cols-2 gap-3 text-left max-w-2xl mx-auto mb-10">
            {['Love food and customer service','Want to build a business in their city or town','Take pride in local ownership and hard work','Value a proven brand but want independence','Are ready to bring something different to the table'].map(t => (
              <li key={t} className="flex gap-3 items-start"><Check className="text-[#ED2E2E] mt-1 shrink-0" size={18} /><span className="text-white/85">{t}</span></li>
            ))}
          </ul>
          <p className="text-white/70 italic">No restaurant background? No stress. If you bring the drive, we’ll bring the rest.</p>
        </div>
      </section>

      <section className="py-20 bg-[#FDF3EC]">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-10">
            <h2 className="section-title text-3xl md:text-5xl mb-3">Let’s Get You Rolling</h2>
            <p className="text-gray-600">Becoming a Wrappus Donair franchise partner means owning a business Canadians will love, in a brand you can be proud of.</p>
          </div>

          <form onSubmit={onSubmit} className="bg-white rounded-3xl p-8 md:p-10 shadow-lg space-y-5">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="First Name *"><input required value={form.firstName} onChange={on('firstName')} className="input-w" /></Field>
              <Field label="Last Name *"><input required value={form.lastName} onChange={on('lastName')} className="input-w" /></Field>
              <Field label="Email Address *"><input required type="email" value={form.email} onChange={on('email')} className="input-w" /></Field>
              <Field label="Contact Number *"><input required value={form.phone} onChange={on('phone')} className="input-w" /></Field>
              <Field label="Current City & Province *"><input required value={form.city} onChange={on('city')} className="input-w" /></Field>
              <Field label="Desired Franchise Location *"><input required value={form.desired} onChange={on('desired')} className="input-w" /></Field>
              <Field label="Preferred investment timeframe *">
                <select required value={form.timeframe} onChange={on('timeframe')} className="input-w">
                  <option value="">Select</option>
                  <option>Within 3 months</option>
                  <option>3–6 months</option>
                  <option>6–12 months</option>
                  <option>12+ months</option>
                </select>
              </Field>
              <Field label="Available investment budget *">
                <select required value={form.budget} onChange={on('budget')} className="input-w">
                  <option value="">Select</option>
                  <option>Under $150K</option>
                  <option>$150K–$250K</option>
                  <option>$250K–$400K</option>
                  <option>$400K+</option>
                </select>
              </Field>
            </div>
            <Field label="Have you ever owned or operated a business before? *">
              <div className="flex gap-4 mt-1">
                {['Yes','No'].map(opt => (
                  <label key={opt} className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="exp" checked={form.experience === opt} onChange={() => setForm({ ...form, experience: opt })} className="accent-[#ED2E2E]" />
                    <span className="text-[#0F1C2E] font-medium">{opt}</span>
                  </label>
                ))}
              </div>
            </Field>
            <Field label="Tell us more if yes (Optional)">
              <textarea rows={2} value={form.experienceDetail} onChange={on('experienceDetail')} className="input-w resize-none" />
            </Field>
            <Field label="What interests you most about franchising with Wrappus Donair?">
              <textarea rows={3} value={form.interest} onChange={on('interest')} className="input-w resize-none" />
            </Field>
            <button type="submit" disabled={submitting} className="btn-wrappus w-full justify-center disabled:opacity-60">
              {submitting ? 'Submitting...' : 'Send Message'}
            </button>
          </form>

          <div className="mt-10 grid md:grid-cols-3 gap-4 text-center text-sm">
            <div className="bg-white rounded-2xl p-5"><div className="text-[#ED2E2E] font-bold">Phone</div><div className="font-semibold text-[#0F1C2E]">{CONTACT.phone}</div></div>
            <div className="bg-white rounded-2xl p-5"><div className="text-[#ED2E2E] font-bold">Email</div><div className="font-semibold text-[#0F1C2E]">{CONTACT.email}</div></div>
            <div className="bg-white rounded-2xl p-5"><div className="text-[#ED2E2E] font-bold">Location</div><div className="font-semibold text-[#0F1C2E]">{CONTACT.address}</div></div>
          </div>
        </div>
      </section>

      <style>{`.input-w{width:100%;border-radius:0.75rem;border:1px solid #E5E7EB;background:#FDF3EC;padding:0.75rem 1rem;font-size:0.95rem;transition:all 0.2s;} .input-w:focus{outline:none;border-color:#ED2E2E;box-shadow:0 0 0 3px rgba(237,46,46,0.15);} `}</style>
    </main>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-[#0F1C2E] mb-1.5">{label}</label>
      {children}
    </div>
  );
}
